import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, X, UserPlus, Shield, ShieldOff, Search, Merge } from "lucide-react";
import { AGE_GROUPS } from "@/lib/tracks";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";

// Households panel — coordinator/org-admin can group directory people (guests)
// into families so a parent can check in dependents during Attendance & Prayer
// flows. Every person belongs to at most one household; any household member
// marked "Guardian" can act on behalf of everyone else in that household.

const orgIdParam = (orgId) => (orgId ? `?org_id=${orgId}` : "");

function NewHouseholdDialog({ orgId, allGuests, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState({});
  const [guardians, setGuardians] = useState({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const eligible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allGuests
      .filter((g) => !g.household_id) // only unattached
      .filter((g) => !ql || `${g.first_name} ${g.last_name}`.toLowerCase().includes(ql))
      .slice(0, 100);
  }, [allGuests, q]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Give this household a name."); return; }
    const memberIds = Object.keys(selected).filter((k) => selected[k]);
    const guardianIds = memberIds.filter((k) => guardians[k]);
    setBusy(true);
    try {
      const r = await api.post(`/coord/households${orgIdParam(orgId)}`, {
        name: name.trim(),
        member_ids: memberIds,
        guardian_ids: guardianIds,
      });
      toast.success("Household created.");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create household.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="household-new-dialog">
      <div className="w-full max-w-lg card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>New household</h3>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="household-new-close"><X size={16} /></button>
        </div>
        <label className="block mb-4">
          <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Household name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Thrash Family"
            className="w-full px-3 py-2 text-sm"
            data-testid="household-new-name"
          />
        </label>
        <div className="mb-2 flex items-center gap-2">
          <Search size={13} style={{ color: "var(--muted)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search directory to add members…"
            className="flex-1 px-3 py-2 text-sm"
            data-testid="household-new-search"
          />
        </div>
        <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-2 flex items-center justify-between" style={{ color: "var(--muted)" }}>
          <span>Members</span>
          <span>Toggle <Shield size={10} className="inline" /> to mark guardians</span>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1 mb-4 pr-2">
          {eligible.length === 0 && (
            <p className="text-xs italic" style={{ color: "var(--muted)" }}>No unassigned people match — everyone might already be in another household.</p>
          )}
          {eligible.map((g) => (
            <div key={g.id} className="flex items-center gap-2 p-2 rounded" style={{ background: selected[g.id] ? "var(--surface-hover)" : "transparent" }}>
              <input
                type="checkbox"
                checked={!!selected[g.id]}
                onChange={(e) => setSelected({ ...selected, [g.id]: e.target.checked })}
                className="h-4 w-4"
                data-testid={`household-new-member-${g.id}`}
              />
              <div className="flex-1 text-sm" style={{ color: "var(--ink)" }}>
                {formatDisplayName(g)}
                {g.age_group && <span className="text-[0.6rem] ml-2" style={{ color: "var(--muted)" }}>{g.age_group}</span>}
              </div>
              {selected[g.id] && (
                <button
                  onClick={() => setGuardians({ ...guardians, [g.id]: !guardians[g.id] })}
                  className="text-[0.6rem] px-2 py-1 rounded"
                  style={{ background: guardians[g.id] ? "var(--ember)" : "transparent", color: guardians[g.id] ? "#FFF" : "var(--ember)", border: `1px solid var(--ember)` }}
                  data-testid={`household-new-guardian-${g.id}`}
                >
                  {guardians[g.id] ? "Guardian" : "Set guardian"}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1" data-testid="household-new-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()} className="btn-primary flex-1" data-testid="household-new-submit">
            {busy ? "Creating…" : "Create household"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddPersonDialog({ orgId, hh, allGuests, onClose, onUpdated }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [q, setQ] = useState("");
  const [existingId, setExistingId] = useState("");
  const [newPerson, setNewPerson] = useState({ first_name: "", last_name: "", gender: null, phone: "", email: "", age_group: "", lifecycle_status: "member" });
  const [asGuardian, setAsGuardian] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null);

  const memberIds = new Set(hh.members.map((m) => m.id));
  const eligible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allGuests
      .filter((g) => !g.household_id && !memberIds.has(g.id))
      .filter((g) => !ql || `${g.first_name} ${g.last_name}`.toLowerCase().includes(ql))
      .slice(0, 40);
  }, [allGuests, q, memberIds]);

  const submitNewPerson = async (force = false) => {
    if (!newPerson.first_name.trim()) { toast.error("First name is required."); setBusy(false); return; }
    setBusy(true);
    try {
      const params = { force };
      if (orgId) params.org_id = orgId;
      const r = await api.post(
        `/coord/households/${hh.id}/new-person`,
        { ...newPerson, is_guardian: asGuardian },
        { params },
      );
      onUpdated(r.data);
      toast.success("Added to household.");
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && d.code === "duplicate_guest") {
        setDupes(d.duplicates || []);
      } else {
        toast.error(typeof d === "string" ? d : "Couldn't add.");
      }
    } finally { setBusy(false); }
  };

  const attachExisting = async (guestId) => {
    setBusy(true);
    try {
      const r = await api.post(`/coord/households/${hh.id}/members${orgIdParam(orgId)}`, { guest_id: guestId, is_guardian: asGuardian });
      onUpdated(r.data);
      toast.success("Added to household.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (mode === "existing") {
      if (!existingId) { toast.error("Pick someone from the list."); return; }
      await attachExisting(existingId);
    } else {
      await submitNewPerson(false);
    }
  };

  if (dupes) {
    // The picked people are candidates who might already be in the directory.
    // Offer to *use* one of them (attaches to household) or continue creating a new profile.
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="household-add-dupes">
        <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Possible duplicate</h3>
            <button onClick={() => setDupes(null)} className="btn-ghost text-xs"><X size={16} /></button>
          </div>
          <p className="text-sm mb-3" style={{ color: "var(--ink-2)" }}>
            Looks like this person may already be in the directory. Use the existing profile or add anyway?
          </p>
          <div className="space-y-2 mb-4 max-h-52 overflow-y-auto">
            {dupes.map((d) => {
              const alreadyInThisHousehold = memberIds.has(d.id);
              const inAnotherHousehold = !!d.household_id && !alreadyInThisHousehold;
              return (
                <div key={d.id} className="p-3 rounded-md text-sm flex items-center justify-between gap-2" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)" }} data-testid={`household-add-dupe-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate" style={{ color: "var(--ink)" }}>{formatDisplayName(d)}</div>
                    <div className="text-[0.65rem] truncate" style={{ color: "var(--muted)" }}>
                      {[d.phone, d.email, d.lifecycle_status].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {alreadyInThisHousehold ? (
                    <span className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Already here</span>
                  ) : inAnotherHousehold ? (
                    <span className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>In another household</span>
                  ) : (
                    <button
                      onClick={() => { setDupes(null); attachExisting(d.id); }}
                      disabled={busy}
                      className="btn-ghost text-xs"
                      style={{ color: "var(--ember)" }}
                      data-testid={`household-add-dupe-use-${d.id}`}
                    >
                      Use this
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setDupes(null)} className="btn-ghost flex-1" data-testid="household-add-dupe-back">Back to form</button>
            <button onClick={() => { setDupes(null); submitNewPerson(true); }} disabled={busy} className="btn-primary flex-1" data-testid="household-add-dupe-continue">
              Add anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="household-add-dialog">
      <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Add to <em>{hh.name}</em></h3>
          <button onClick={onClose} className="btn-ghost text-xs"><X size={16} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode("existing")}
            className="flex-1 text-xs py-2 rounded"
            style={{ background: mode === "existing" ? "var(--ember)" : "transparent", color: mode === "existing" ? "#FFF" : "var(--ink-2)", border: `1px solid var(--border-subtle)` }}
            data-testid="household-add-mode-existing"
          >
            Existing person
          </button>
          <button
            onClick={() => setMode("new")}
            className="flex-1 text-xs py-2 rounded"
            style={{ background: mode === "new" ? "var(--ember)" : "transparent", color: mode === "new" ? "#FFF" : "var(--ink-2)", border: `1px solid var(--border-subtle)` }}
            data-testid="household-add-mode-new"
          >
            New person
          </button>
        </div>

        {mode === "existing" ? (
          <>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search directory…"
              className="w-full px-3 py-2 text-sm mb-2"
              data-testid="household-add-search"
            />
            <div className="max-h-60 overflow-y-auto space-y-1 mb-4">
              {eligible.length === 0 && <p className="text-xs italic" style={{ color: "var(--muted)" }}>No one available.</p>}
              {eligible.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setExistingId(g.id)}
                  className="w-full text-left p-2 rounded flex items-center justify-between text-sm"
                  style={{ background: existingId === g.id ? "var(--surface-hover)" : "transparent", color: "var(--ink)" }}
                  data-testid={`household-add-existing-${g.id}`}
                >
                  <span>{formatDisplayName(g)}</span>
                  {g.age_group && <span className="text-[0.6rem]" style={{ color: "var(--muted)" }}>{g.age_group}</span>}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="First name *" value={newPerson.first_name} onChange={(e) => setNewPerson({ ...newPerson, first_name: e.target.value })} className="px-3 py-2 text-sm" data-testid="household-add-new-first" />
              <input placeholder="Last name" value={newPerson.last_name} onChange={(e) => setNewPerson({ ...newPerson, last_name: e.target.value })} className="px-3 py-2 text-sm" data-testid="household-add-new-last" />
            </div>
            <input placeholder="Phone" value={newPerson.phone} onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="household-add-new-phone" />
            <select value={newPerson.age_group} onChange={(e) => setNewPerson({ ...newPerson, age_group: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="household-add-new-age">
              <option value="">Age group…</option>
              {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <GenderField value={newPerson.gender} onChange={(g) => setNewPerson({ ...newPerson, gender: g })} testid="household-add-new-gender" />
            <label className="block">
              <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Type</span>
              <select
                value={newPerson.lifecycle_status}
                onChange={(e) => setNewPerson({ ...newPerson, lifecycle_status: e.target.value })}
                className="w-full px-3 py-2 text-sm"
                data-testid="household-add-new-lifecycle"
              >
                <option value="member">Member (church family — stays out of the guest tracker)</option>
                <option value="disciple">Disciple (already in the Next Steps journey)</option>
                <option value="guest">Guest (still in evangelism / First Steps)</option>
              </select>
            </label>
          </div>
        )}

        <label className="flex items-center gap-2 mb-4 text-sm" style={{ color: "var(--ink-2)" }}>
          <input type="checkbox" checked={asGuardian} onChange={(e) => setAsGuardian(e.target.checked)} className="h-4 w-4" data-testid="household-add-guardian" />
          <span>Guardian (can check in the whole family)</span>
        </label>

        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary flex-1" data-testid="household-add-submit">
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MergeDialog({ orgId, hh, allHouseholds, onClose, onMerged }) {
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const options = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allHouseholds
      .filter((h) => h.id !== hh.id)
      .filter((h) => !ql || h.name.toLowerCase().includes(ql) || h.members.some((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(ql)))
      .slice(0, 30);
  }, [allHouseholds, q, hh.id]);

  const target = options.find((h) => h.id === pick);

  const submit = async () => {
    if (!pick) { toast.error("Pick a household to merge into."); return; }
    if (!window.confirm(`Merge "${hh.name}" INTO "${target?.name}"? All ${hh.members.length} members move over and "${hh.name}" is deleted. This can't be undone.`)) return;
    setBusy(true);
    try {
      const params = {};
      if (orgId) params.org_id = orgId;
      const r = await api.post(`/coord/households/${hh.id}/merge`, { into_id: pick }, { params });
      toast.success(`Merged into ${target?.name}.`);
      onMerged(hh.id, r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't merge.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="household-merge-dialog">
      <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Merge <em>{hh.name}</em></h3>
          <button onClick={onClose} className="btn-ghost text-xs"><X size={16} /></button>
        </div>
        <p className="text-sm mb-3" style={{ color: "var(--ink-2)" }}>
          Pick which household to keep. All {hh.members.length} member{hh.members.length === 1 ? "" : "s"} of <em>{hh.name}</em> will move into it, and <em>{hh.name}</em> will be deleted.
        </p>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search households…"
          className="w-full px-3 py-2 text-sm mb-2"
          data-testid="household-merge-search"
        />
        <div className="max-h-60 overflow-y-auto space-y-1 mb-4">
          {options.length === 0 && <p className="text-xs italic" style={{ color: "var(--muted)" }}>No other households to merge with yet.</p>}
          {options.map((h) => (
            <button
              key={h.id}
              onClick={() => setPick(h.id)}
              className="w-full text-left p-3 rounded flex flex-col gap-1"
              style={{ background: pick === h.id ? "var(--surface-hover)" : "transparent", border: `1px solid ${pick === h.id ? "var(--ember)" : "var(--border-subtle)"}`, color: "var(--ink)" }}
              data-testid={`household-merge-target-${h.id}`}
            >
              <span className="font-medium text-sm">{h.name}</span>
              <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>
                {h.members.length === 0 ? "empty" : h.members.slice(0, 4).map((m) => formatDisplayName(m)).join(", ") + (h.members.length > 4 ? "…" : "")}
              </span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button onClick={submit} disabled={busy || !pick} className="btn-primary flex-1" data-testid="household-merge-submit">
            {busy ? "Merging…" : "Merge"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HouseholdCard({ hh, orgId, allGuests, allHouseholds, onChanged, onDeleted, onMerged }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(hh.name);
  const [addOpen, setAddOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const saveName = async () => {
    const n = name.trim();
    if (!n || n === hh.name) { setEditingName(false); setName(hh.name); return; }
    try {
      const r = await api.put(`/coord/households/${hh.id}${orgIdParam(orgId)}`, { name: n });
      toast.success("Renamed.");
      onChanged(r.data);
      setEditingName(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't rename.");
    }
  };

  const toggleGuardian = async (m) => {
    try {
      const r = await api.put(`/coord/households/${hh.id}/members/${m.id}${orgIdParam(orgId)}`, {
        guest_id: m.id, is_guardian: !m.is_guardian,
      });
      onChanged(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${formatDisplayName(m)} from this household? Their directory profile stays.`)) return;
    try {
      const r = await api.delete(`/coord/households/${hh.id}/members/${m.id}${orgIdParam(orgId)}`);
      onChanged(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove.");
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete household "${hh.name}"? Members stay in the directory but are un-grouped.`)) return;
    try {
      await api.delete(`/coord/households/${hh.id}${orgIdParam(orgId)}`);
      toast.success("Household deleted.");
      onDeleted(hh.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <div className="card-surface p-5" style={{ background: "var(--bg-alt)" }} data-testid={`household-card-${hh.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Users size={16} style={{ color: "var(--ember)" }} />
          {editingName ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setName(hh.name); } }}
              className="px-2 py-1 text-lg font-serif-display"
              data-testid={`household-rename-${hh.id}`}
            />
          ) : (
            <button onClick={() => setEditingName(true)} className="font-serif-display text-xl truncate" style={{ color: "var(--ink)" }} data-testid={`household-name-${hh.id}`}>
              {hh.name}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAddOpen(true)} className="btn-ghost text-xs" data-testid={`household-add-btn-${hh.id}`}>
            <UserPlus size={13} /> Add
          </button>
          <button onClick={() => setMergeOpen(true)} className="btn-ghost text-xs" data-testid={`household-merge-btn-${hh.id}`} title="Merge into another household">
            <Merge size={13} />
          </button>
          <button onClick={del} className="btn-ghost text-xs" style={{ color: "var(--terracotta)" }} data-testid={`household-delete-${hh.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {hh.members.length === 0 ? (
        <p className="text-xs italic" style={{ color: "var(--muted)" }}>No members yet — add someone from the directory.</p>
      ) : (
        <div className="space-y-1.5">
          {hh.members.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-2 p-2 rounded" style={{ background: "var(--surface-hover)" }}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-sm truncate" style={{ color: "var(--ink)" }}>
                  {formatDisplayName(m)}
                </span>
                {m.age_group && <span className="text-[0.6rem]" style={{ color: "var(--muted)" }}>{m.age_group}</span>}
                {m.is_guardian && <span className="text-[0.55rem] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded" style={{ background: "var(--ember)", color: "#FFF" }}>Guardian</span>}
              </div>
              <button
                onClick={() => toggleGuardian(m)}
                className="text-[0.65rem] px-2 py-1 rounded"
                style={{ color: m.is_guardian ? "var(--muted)" : "var(--ember)", border: "1px solid var(--border-subtle)" }}
                data-testid={`household-toggle-guardian-${m.id}`}
                title={m.is_guardian ? "Revoke guardian" : "Make guardian"}
              >
                {m.is_guardian ? <ShieldOff size={12} /> : <Shield size={12} />}
              </button>
              <button
                onClick={() => removeMember(m)}
                className="text-[0.65rem] px-2 py-1 rounded"
                style={{ color: "var(--terracotta)", border: "1px solid var(--border-subtle)" }}
                data-testid={`household-remove-member-${m.id}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <AddPersonDialog orgId={orgId} hh={hh} allGuests={allGuests} onClose={() => setAddOpen(false)} onUpdated={onChanged} />
      )}
      {mergeOpen && (
        <MergeDialog orgId={orgId} hh={hh} allHouseholds={allHouseholds} onClose={() => setMergeOpen(false)} onMerged={(fromId, kept) => { setMergeOpen(false); onMerged(fromId, kept); }} />
      )}
    </div>
  );
}

export default function HouseholdsPanel({ orgId }) {
  const [items, setItems] = useState(null);
  const [allGuests, setAllGuests] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [hhs, gs] = await Promise.all([
        api.get(`/coord/households${orgIdParam(orgId)}`),
        api.get(`/coord/guests${orgIdParam(orgId)}`),
      ]);
      setItems(hhs.data || []);
      setAllGuests(gs.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load households.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const upsert = (hh) => {
    setItems((prev) => {
      const rest = (prev || []).filter((x) => x.id !== hh.id);
      return [hh, ...rest].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    });
    // Reload directory in case membership counts changed
    api.get(`/coord/guests${orgIdParam(orgId)}`).then((r) => setAllGuests(r.data || [])).catch(() => {});
  };

  const removeLocal = (hid) => setItems((prev) => (prev || []).filter((x) => x.id !== hid));

  const handleMerged = (fromId, kept) => {
    // Remove the merged-source household; the target's hydrated data is `kept`.
    setItems((prev) => {
      const filtered = (prev || []).filter((x) => x.id !== fromId && x.id !== kept.id);
      return [kept, ...filtered].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    });
    api.get(`/coord/guests${orgIdParam(orgId)}`).then((r) => setAllGuests(r.data || [])).catch(() => {});
  };

  const filtered = useMemo(() => {
    if (!items) return null;
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((h) => (h.name || "").toLowerCase().includes(ql) || h.members.some((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(ql)));
  }, [items, q]);

  if (items === null) {
    return <p className="text-sm" style={{ color: "var(--muted)" }}>Loading households…</p>;
  }

  return (
    <div data-testid="households-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Households</h2>
          <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
            Group family members so a parent can check in dependents for attendance & prayer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search households…"
              className="pl-7 pr-3 py-2 text-sm rounded"
              data-testid="households-search"
            />
          </div>
          <button onClick={() => setNewOpen(true)} className="btn-primary text-sm" data-testid="households-new-btn">
            <Plus size={14} /> New household
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 card-surface" style={{ background: "var(--bg-alt)" }}>
          <Users size={32} style={{ color: "var(--muted)" }} className="mx-auto mb-3" />
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>No households yet.</p>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Create one so parents can check in their kids at attendance & prayer.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((hh) => (
            <HouseholdCard
              key={hh.id}
              hh={hh}
              orgId={orgId}
              allGuests={allGuests}
              allHouseholds={items || []}
              onChanged={upsert}
              onDeleted={removeLocal}
              onMerged={handleMerged}
            />
          ))}
        </div>
      )}

      {newOpen && (
        <NewHouseholdDialog
          orgId={orgId}
          allGuests={allGuests}
          onClose={() => setNewOpen(false)}
          onCreated={(hh) => { upsert(hh); setNewOpen(false); }}
        />
      )}
    </div>
  );
}
